import {
  AccountBalance,
  AccountBalanceWallet,
  ExpandMore,
  MoreVert,
  ReceiptLong,
} from "@mui/icons-material";
import { IconButton } from "@mui/material";
import moment from "moment";
import "moment/locale/pt-br";
import { useEffect, useMemo, useRef, useState } from "react";
import intl from "react-intl-universal";
import { Link } from "react-router-dom";
import { MdOutlinePix } from "react-icons/md";
import { TbEye, TbReceiptRefund } from "react-icons/tb";
import {
  formatCurrency,
} from "../../../Utils/formatters";
import { mapStatementTransactionRow } from "../../../Utils/mapStatementTransactionRow";
import {
  buildPixBatchStatementItem,
  getStatementBatchId,
  getStatementLegCount,
  groupDayItemsByPixBatch,
} from "../../../Utils/groupStatementItemsByPixBatch";
import { buildReceiptFromStatementItem } from "../../../Utils/buildReceiptFromStatementItem";
import {
  resolveStatementCategoryIcon,
} from "../BaasConsole/Extrato/baasCdnIcons";
import { canRefundPixRecebimento } from "../Pix/pixStatementFilters";
import { usePixStatementDetailFlow } from "../Pix/usePixStatementDetailFlow";
import { getExtractTransactionAmountCents, isExtractCreditTransaction } from "./extractFilters";

moment.locale("pt-br");

const formatSignedCurrency = (value) => {
  const formatted = formatCurrency(Math.abs(value));
  if (value > 0) return `+${formatted}`;
  if (value < 0) return `-${formatted}`;
  return formatted;
};

const DateBubble = ({ day, isLast }) => (
  <div className="extract-timeline__date-col">
    <div className="extract-timeline__bubble" aria-hidden>
      <span className="extract-timeline__bubble-day">{moment(day).format("DD")}</span>
      <span className="extract-timeline__bubble-month">{moment(day).format("MMM")}</span>
      <span className="extract-timeline__bubble-weekday">{moment(day).format("ddd")}</span>
    </div>
    {!isLast && <span className="extract-timeline__line" aria-hidden />}
  </div>
);

const TransactionIcon = ({ src }) => {
  const resolved = resolveStatementCategoryIcon(src);
  const [broken, setBroken] = useState(false);
  const isBrandPix =
    resolved && /static\.valut\.app\/balance-icons\/pix\.svg/i.test(resolved);
  const isValutBalanceIcon =
    resolved && /static\.valut\.app\/balance-icons\//i.test(resolved);
  const isCircularCdn =
    !broken &&
    resolved &&
    !isBrandPix &&
    !isValutBalanceIcon &&
    (/cdn\.valut\.services\/assets\/icons\//i.test(resolved) ||
      /\/cdn\/icons\//i.test(resolved));
  return (
    <span className="extract-timeline__tx-icon">
      {resolved && !broken ? (
        <img
          src={resolved}
          alt=""
          onError={() => setBroken(true)}
          className={
            isCircularCdn
              ? "h-9 w-9 rounded-full object-cover"
              : "h-4 w-auto max-w-[18px]"
          }
        />
      ) : (
        <MdOutlinePix className="h-5 w-5 text-[#e7a73f]" aria-hidden />
      )}
    </span>
  );
};

const TransactionRowMenu = ({
  item,
  refundEnabled,
  onShowDetails,
  onRefund,
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const canRefund = canRefundPixRecebimento(item, { refundEnabled });

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <IconButton
        size="small"
        className="extract-timeline__menu-btn"
        aria-label={intl.get("common.details")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((current) => !current);
        }}
      >
        <MoreVert sx={{ fontSize: 18 }} />
      </IconButton>

      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-20 mt-1 min-w-[10.5rem] overflow-hidden rounded-xl border border-gray-200 bg-white py-1 shadow-card dark:border-brand-dark-border dark:bg-brand-dark-elevated"
        >
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-brand-dark-border/40"
            onClick={(event) => {
              event.stopPropagation();
              setOpen(false);
              onShowDetails(item);
            }}
          >
            <TbEye className="h-4 w-4 shrink-0" />
            {intl.get("extract.viewDetails")}
          </button>
          {canRefund ? (
            <button
              type="button"
              role="menuitem"
              className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-brand-dark hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-brand-dark-border/40"
              onClick={(event) => {
                event.stopPropagation();
                setOpen(false);
                onRefund(item);
              }}
            >
              <TbReceiptRefund className="h-4 w-4 shrink-0" />
              {intl.get("pix.refund.action")}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

const LoteChipLink = ({ batchId, label }) => {
  if (!batchId || !label) return null;

  return (
    <Link
      to={`/pix/lotes/${encodeURIComponent(batchId)}`}
      className="extract-timeline__lote-chip extract-timeline__lote-chip--link"
      onClick={(event) => event.stopPropagation()}
    >
      {label}
    </Link>
  );
};

const TransactionRow = ({
  item,
  refundEnabled,
  onShowDetails,
  onRefund,
  loteChip = null,
  isLeg = false,
  onRowClick = undefined,
}) => {
  const isCredit = isExtractCreditTransaction(item);
  const signedAmount = isCredit
    ? getExtractTransactionAmountCents(item)
    : -getExtractTransactionAmountCents(item);
  const { title, subtitle, meta } = mapStatementTransactionRow(item);
  const handleRowActivate = onRowClick || (() => onShowDetails(item));
  const batchId = getStatementBatchId(item);
  const legCount = getStatementLegCount(item);
  const loteLabel =
    loteChip
    ?? (batchId ? (legCount > 1 ? `Lote ${legCount}x` : "Lote em andamento") : null);

  return (
    <div
      className={`extract-timeline__tx-row${isLeg ? " extract-timeline__tx-row--leg" : ""}`}
      onClick={handleRowActivate}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          handleRowActivate();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <TransactionIcon src={item?.category?.icon} />

      <div className="extract-timeline__tx-main min-w-0 flex-1">
        <p className="extract-timeline__tx-title">{title}</p>
        {subtitle ? <p className="extract-timeline__tx-subtitle">{subtitle}</p> : null}
        <div className="extract-timeline__tx-meta">
          {loteLabel && batchId ? (
            <LoteChipLink batchId={batchId} label={loteLabel} />
          ) : loteLabel ? (
            <span className="extract-timeline__lote-chip">{loteLabel}</span>
          ) : null}
          {meta ? <span>{meta}</span> : null}
        </div>
      </div>

      <div className="extract-timeline__tx-actions">
        <div className="extract-timeline__tx-amount-row">
          <p
            className={`extract-timeline__tx-amount ${
              isCredit
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {formatSignedCurrency(signedAmount)}
          </p>
          <TransactionRowMenu
            item={item}
            refundEnabled={refundEnabled}
            onShowDetails={onShowDetails}
            onRefund={onRefund}
          />
        </div>
      </div>
    </div>
  );
};

const BatchTransactionGroup = ({
  legs,
  refundEnabled,
  onShowDetails,
  onRefund,
}) => {
  const [expanded, setExpanded] = useState(false);
  const collapsed = useMemo(() => buildPixBatchStatementItem(legs), [legs]);
  const count = collapsed?.leg_count || legs.length;
  const receiptItem = useMemo(() => {
    if (!collapsed) return null;
    return {
      ...collapsed,
      receipt: buildReceiptFromStatementItem(collapsed),
    };
  }, [collapsed]);

  if (!collapsed) return null;

  return (
    <div>
      <TransactionRow
        item={collapsed}
        refundEnabled={false}
        onShowDetails={() => onShowDetails(receiptItem)}
        onRefund={onRefund}
        loteChip={`Lote ${count}x`}
        onRowClick={() => setExpanded((current) => !current)}
      />
      {expanded
        ? legs.map((leg, index) => (
            <TransactionRow
              key={leg?.id ?? `${collapsed.batch_id}-${index}`}
              item={leg}
              refundEnabled={refundEnabled}
              onShowDetails={onShowDetails}
              onRefund={onRefund}
              isLeg
              loteChip={`${leg?.leg_index ?? index + 1}/${count}`}
            />
          ))
        : null}
    </div>
  );
};

const formatDayBalance = (raw) => {
  if (raw == null || raw === "") return "—";
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return "—";
  return formatCurrency(Math.abs(numeric));
};

/** Opening/closing of the calendar day, independent of UI list order (often newest-first). */
const resolveDayOpeningClosingBalances = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) {
    return { opening: null, closing: null };
  }
  const sorted = [...items].sort((a, b) => {
    const ta = moment(a?.created_at).valueOf();
    const tb = moment(b?.created_at).valueOf();
    if (Number.isFinite(ta) && Number.isFinite(tb) && ta !== tb) return ta - tb;
    return String(a?.id ?? "").localeCompare(String(b?.id ?? ""));
  });
  return {
    opening: sorted[0]?.balance_before,
    closing: sorted[sorted.length - 1]?.balance_after,
  };
};

const DayCard = ({
  day,
  items,
  expanded,
  onToggle,
  refundEnabled,
  onShowDetails,
  onRefund,
}) => {
  const { opening, closing } = resolveDayOpeningClosingBalances(items);
  const openingBalance = formatDayBalance(opening);
  const closingBalance = formatDayBalance(closing);
  const groupedItems = groupDayItemsByPixBatch(items);
  const transactionCount = groupedItems.length;

  return (
    <div className="extract-timeline__card">
      <button
        type="button"
        className="extract-timeline__card-header"
        onClick={() => onToggle(day)}
        aria-expanded={expanded}
      >
        <div className="extract-timeline__balance-pair min-w-0 flex-1">
          <div className="extract-timeline__balance-item">
            <span className="extract-timeline__balance-icon" aria-hidden>
              <AccountBalanceWallet sx={{ fontSize: 16 }} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="extract-timeline__balance-label">
                {intl.get("extract.openingDayBalance")}
              </span>
              <span className="extract-timeline__balance-value">{openingBalance}</span>
            </div>
          </div>
          <span className="extract-timeline__balance-sep" aria-hidden>
            |
          </span>
          <div className="extract-timeline__balance-item">
            <span className="extract-timeline__balance-icon" aria-hidden>
              <AccountBalance sx={{ fontSize: 16 }} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="extract-timeline__balance-label">
                {intl.get("extract.closingDayBalance")}
              </span>
              <span className="extract-timeline__balance-value">{closingBalance}</span>
            </div>
          </div>
          <span className="extract-timeline__balance-sep" aria-hidden>
            |
          </span>
          <div className="extract-timeline__balance-item">
            <span className="extract-timeline__balance-icon" aria-hidden>
              <ReceiptLong sx={{ fontSize: 16 }} />
            </span>
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="extract-timeline__balance-label">
                {intl.get("extract.transactionCount")}
              </span>
              <span className="extract-timeline__balance-value">
                {intl.get("extract.entriesCount", { count: transactionCount })}
              </span>
            </div>
          </div>
        </div>
        <ExpandMore
          className={`extract-timeline__chevron ${expanded ? "extract-timeline__chevron--open" : ""}`}
          sx={{ fontSize: 22 }}
        />
      </button>

      {expanded ? (
        <div className="extract-timeline__card-body">
          {groupedItems.map((group, index) =>
            group.type === "batch" ? (
              <BatchTransactionGroup
                key={group.batchId ?? `${day}-batch-${index}`}
                legs={group.legs}
                refundEnabled={refundEnabled}
                onShowDetails={onShowDetails}
                onRefund={onRefund}
              />
            ) : (
              <TransactionRow
                key={group.item?.id ?? `${day}-${index}`}
                item={group.item}
                refundEnabled={refundEnabled}
                onShowDetails={onShowDetails}
                onRefund={onRefund}
              />
            ),
          )}
        </div>
      ) : null}
    </div>
  );
};

/**
 * @param {object} props
 * @param {Array} [props.dayGroups]
 * @param {boolean} [props.allExpanded] - When provided, controls expand-all state externally (Extract page).
 * @param {(expanded: boolean) => void} [props.onAllExpandedChange] - Syncs per-day toggles back to parent.
 * @param {boolean} [props.refundEnabled=true] - Show Estornar for Pix recebimento rows.
 * @param {() => void} [props.onRefundSuccess] - Called after a successful Pix refund.
 */
export const ExtractDayTimeline = ({
  dayGroups = [],
  allExpanded = undefined,
  onAllExpandedChange = undefined,
  refundEnabled = true,
  onRefundSuccess = undefined,
}) => {
  const { openDetails, openRefund, detailModals } = usePixStatementDetailFlow({
    refundEnabled,
    onRefundSuccess,
  });

  const dayKeys = useMemo(
    () =>
      dayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [dayGroups],
  );

  const [expandedDays, setExpandedDays] = useState(() => new Set());
  const lastInitializedDayKeysRef = useRef(null);

  useEffect(() => {
    if (!dayKeys) return;

    const days = dayKeys.split("|");

    if (allExpanded !== undefined) {
      setExpandedDays(allExpanded ? new Set(days) : new Set());
      lastInitializedDayKeysRef.current = dayKeys;
      return;
    }

    if (lastInitializedDayKeysRef.current === dayKeys) return;

    lastInitializedDayKeysRef.current = dayKeys;
    setExpandedDays(new Set(days));
  }, [dayKeys, allExpanded]);

  const toggleDay = (day) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) {
        next.delete(day);
      } else {
        next.add(day);
      }

      if (onAllExpandedChange && dayKeys) {
        const allDays = dayKeys.split("|");
        const everyExpanded =
          allDays.length > 0 && allDays.every((dayKey) => next.has(dayKey));
        onAllExpandedChange(everyExpanded);
      }

      return next;
    });
  };

  if (dayGroups.length === 0) {
    return <div className="app-empty-state m-4">{intl.get("common.noRecordsFiltered")}</div>;
  }

  const validGroups = dayGroups
    .map((group) => {
      if (!group) return null;
      const day = Object.keys(group)[0];
      const items = group[day] ?? [];
      if (items.length === 0) return null;
      return { day, items };
    })
    .filter(Boolean);

  return (
    <>
      <div className="extract-timeline">
        {validGroups.map(({ day, items }, index) => (
          <div key={day} className="extract-timeline__row">
            <DateBubble day={day} isLast={index === validGroups.length - 1} />
            <DayCard
              day={day}
              items={items}
              expanded={expandedDays.has(day)}
              onToggle={toggleDay}
              refundEnabled={refundEnabled}
              onShowDetails={openDetails}
              onRefund={openRefund}
            />
          </div>
        ))}
      </div>

      {detailModals}
    </>
  );
};
